import { AgentBoardResource } from "api/resource/agent-board";
import { AgentMlsIdResource } from "api/resource/agent-mls-id";
import { ClientBoardListResource, ClientBoardResource } from "api/resource/client-board";
import { MlsIdAgent } from "model/agent-mls-ids";

// Sample MLS ID accounts for the MLS ID tests, and mlsIdReads, which mock.ts also uses. Each account is its rows: agents,
// Featured rows (client boards) and agents' MLS IDs (AgentBoard rows). The agent MLS ID read is worked out from them.

// An AgentBoard row, with its opt-out flag and the Backup Office MLS ID it holds while off Featured.
export interface MlsIdAgentBoard extends AgentBoardResource {
	optOut?: boolean;
	alternateId?: string | null;
}

export interface MlsIdAccount {
	officeAccount: boolean;
	agents: MlsIdAgent[];
	clientBoards: ClientBoardResource[];
	agentBoards: MlsIdAgentBoard[];
}

// The GET responses the MLS ID pages read.
interface MlsIdReads {
	agentMlsIds: AgentMlsIdResource[];
	agents: MlsIdAgent[];
	clientBoards: ClientBoardListResource;
	agentBoards: AgentBoardResource[];
}

const boardNames: Record<number, string> = { 1: "Sun Valley MLS", 2: "Bellevue MLS", 3: "Tri-Cities MLS" };

const clientBoard = (id: number, boardId: number, agentId = "", officeId = ""): ClientBoardResource =>
	({ id, boardId, boardName: boardNames[boardId], agentId, officeId, status: "APPROVED", paperworkReceived: false });

const agentBoard = (id: number, agentId: number, boardId: number, agentCode: string): MlsIdAgentBoard => ({ id, agentId, boardId, agentCode });

// Every agent's MLS ID is on Featured once, and Elena Park isn't billable.
export const clean: MlsIdAccount = {
	officeAccount: false,
	agents: [
		{ id: 101, name: "Jane Doe", billable: true },
		{ id: 102, name: "Marcus Webb", billable: true },
		{ id: 103, name: "Priya Raman", billable: true },
		{ id: 104, name: "Elena Park", billable: false },
	],
	clientBoards: [
		clientBoard(1001, 1, "JDOE01", "SVRE01"),
		clientBoard(1002, 1, "MWEBB", "SVRE01"),
		clientBoard(1003, 2, "01MAWE02", "BVKW10"),
		clientBoard(1004, 3, "PRAMAN", "TCREM3"),
	],
	agentBoards: [
		agentBoard(1001, 101, 1, "JDOE01"),
		agentBoard(1002, 102, 1, "MWEBB"),
		agentBoard(1003, 102, 2, "01MAWE02"),
		agentBoard(1004, 103, 3, "PRAMAN"),
		agentBoard(1005, 104, 1, "EPARK7"),
	],
};

// Every state. Jane Doe is aligned on Sun Valley. Marcus Webb has MWEBB twice there, each row with its Featured row,
// plus SV448213 and WEBB22 with none (strays), and WEBBMA on Tri-Cities with none. Hector Ruiz has two strays on
// Bellevue, and shares PRTEAM on Tri-Cities with Priya Raman (one Featured row between them). Elena Park isn't billable,
// so her EPARK7 row on Featured is Not Billable. MAWEBB, SPARAGAR and RTORRES4 are on Featured with no agent (orphans).
export const messy: MlsIdAccount = {
	officeAccount: false,
	agents: [
		{ id: 301, name: "Jane Doe", billable: true },
		{ id: 302, name: "Marcus Webb", billable: true },
		{ id: 303, name: "Priya Raman", billable: true },
		{ id: 304, name: "Elena Park", billable: false },
		{ id: 305, name: "Hector Ruiz", billable: true },
	],
	clientBoards: [
		clientBoard(3001, 1, "JDOE01", "SVRE01"),
		clientBoard(3002, 1, "MWEBB", "SVRE01"),
		clientBoard(3003, 1, "MAWEBB"),
		clientBoard(3004, 2, "01MAWE02", "BVKW10"),
		clientBoard(3005, 2, "EPARK7"),
		clientBoard(3006, 2, "SPARAGAR"),
		clientBoard(3007, 3, "PRTEAM", "TCREM3"),
		clientBoard(3008, 1),
		clientBoard(3009, 1, "MWEBB"),
		clientBoard(3010, 1, "RTORRES4", "SVRE01"),
	],
	agentBoards: [
		agentBoard(3001, 301, 1, "JDOE01"),
		agentBoard(3002, 302, 1, "MWEBB"),
		agentBoard(3003, 302, 1, "MWEBB"),
		agentBoard(3004, 302, 1, "SV448213"),
		agentBoard(3005, 302, 1, "WEBB22"),
		agentBoard(3006, 302, 2, "01MAWE02"),
		agentBoard(3007, 302, 3, "WEBBMA"),
		agentBoard(3008, 303, 3, "PRTEAM"),
		agentBoard(3009, 305, 2, "HRUIZ"),
		agentBoard(3010, 305, 2, "HRUIZ22"),
		agentBoard(3011, 305, 3, "PRTEAM"),
		agentBoard(3012, 304, 2, "EPARK7"),
	],
};

// Messy's agents and MLS IDs on an account whose Featured Listings come from Office MLS IDs, one per board.
export const office: MlsIdAccount = {
	...messy,
	officeAccount: true,
	clientBoards: [
		clientBoard(3101, 1, "SVRE01"),
		clientBoard(3102, 2, "BVKW10"),
		clientBoard(3103, 3, "TCREM3"),
	],
};

// Edits for test scenarios.
export const withAgentBoards = (account: MlsIdAccount, ...rows: MlsIdAgentBoard[]): MlsIdAccount =>
	({ ...account, agentBoards: [...account.agentBoards, ...rows] });

export const withoutAgentBoards = (account: MlsIdAccount, ...ids: number[]): MlsIdAccount =>
	({ ...account, agentBoards: account.agentBoards.filter((row) => !ids.includes(row.id)) });

// Featured rows set to "None": still there, off Featured.
export const toNone = (account: MlsIdAccount, ...clientBoardIds: number[]): MlsIdAccount =>
	({ ...account, clientBoards: account.clientBoards.map((row) => (clientBoardIds.includes(row.id) ? { ...row, agentId: "", officeId: "" } : row)) });

// An AgentBoard row turned off Featured, holding a Backup Office MLS ID.
export const optedOut = (account: MlsIdAccount, agentBoardId: number, alternateId: string | null): MlsIdAccount =>
	({ ...account, agentBoards: account.agentBoards.map((row) => (row.id === agentBoardId ? { ...row, optOut: true, alternateId } : row)) });

// The account's reads. The agent MLS ID read is worked out from the rows the way the service does it, so a scenario
// that adds or changes a row gets a read that agrees with itself. This stands in for the server, so unlike the rest of
// Control Panel it compares MLS ID strings (trimmed and case-insensitive, as the service does).
export const mlsIdReads = (account: MlsIdAccount): MlsIdReads => {
	const none = (value: string) => ["", "none"].includes(value.trim().toLowerCase());
	const key = (boardId: number, value: string) => `${boardId}|${value.trim().toLowerCase()}`;
	const billable = new Map(account.agents.map((agent) => [agent.id, agent.billable]));
	const slots = new Map<string, number[]>();
	account.clientBoards.filter((row) => !none(row.agentId)).forEach((row) => {
		const k = key(row.boardId, row.agentId);
		slots.set(k, [...(slots.get(k) ?? []), row.id].sort((a, b) => a - b));
	});
	const live = account.agentBoards.filter((row) => !none(row.agentCode)).sort((a, b) => a.id - b.id);
	const needsSlot = (row: MlsIdAgentBoard) => !account.officeAccount && !!billable.get(row.agentId) && !row.optOut;
	const agentMlsIds = live.map((row): AgentMlsIdResource => {
		const same = live.filter((other) => key(other.boardId, other.agentCode) === key(row.boardId, row.agentCode));
		const holderCount = same.filter(needsSlot).length;
		const clientBoardIds = slots.get(key(row.boardId, row.agentCode)) ?? [];
		return {
			id: row.id,
			agentId: row.agentId,
			boardId: row.boardId,
			mlsId: row.agentCode,
			optOut: !!row.optOut,
			alternateId: row.alternateId ?? null,
			clientBoardIds,
			otherAgentIds: Array.from(new Set(same.map((other) => other.agentId).filter((id) => id !== row.agentId))).sort((a, b) => a - b),
			needsSlot: needsSlot(row),
			holderCount,
			aligned: account.officeAccount || holderCount === clientBoardIds.length,
			valueRowIds: same.map((other) => other.id),
		};
	});
	return {
		agentMlsIds,
		agents: account.agents,
		clientBoards: { officeAccount: account.officeAccount, clientBoards: account.clientBoards },
		agentBoards: account.agentBoards.map(({ id, agentId, boardId, agentCode }) => ({ id, agentId, boardId, agentCode })),
	};
};
